import {
  BackEndValueType,
  CommonHelperFunctions,
  EDataBaseType,
  EDatasourceType,
  ESdChangeReason,
  IAction,
  IActionParameter,
  IDatabaseColumnModel,
  IDatabaseTableModel,
  IDatasourceDto,
  IImportMappedExcelColumnMap,
  IImportMappedExcelParameter,
  IImportMappedExcelSetupWizardConfig
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { ChangeEvent, PureComponent, ReactNode } from "react";
import LocalStorageService from "../../../auth/local-storage-service";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../ui-kit/kuika-table/kuika-table";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH8 from "../../ui-kit/typography/H8/H8";
import KuikaSmallMediumText from "../../ui-kit/typography/SmallMediumText/SmallMediumText";
import "./import-mapped-excel-setup-wizard.scss";

/** The most custom parameters the action can carry — it has placeholders a5–a19. */
const MAX_PARAMETER_COUNT: number = 15;

/** A value the Default Value field offers: a platform token or one of the wizard's parameters. */
interface IDefaultValueOption {
  value: string;
  label: string;
}

export interface IImportMappedExcelSetupWizardProps {}

export interface IImportMappedExcelSetupWizardState {
  isTablesLoading: boolean;
  tables: IDatabaseTableModel[];
  config: IImportMappedExcelSetupWizardConfig;
}

class ImportMappedExcelSetupWizard extends PureComponent<
  IImportMappedExcelSetupWizardProps & IWithSDProps,
  IImportMappedExcelSetupWizardState
> {
  constructor(props: IImportMappedExcelSetupWizardProps & IWithSDProps) {
    super(props);

    // Migrate parameters to ensure they all have IDs
    const parameters = (props.sd.importMappedExcelSetupWizardConfig?.parameters ?? []).map((param) => {
      if (!param.id) {
        return {
          ...param,
          id: _.uniqueId("param_")
        };
      }
      return param;
    });

    this.state = {
      isTablesLoading: false,
      tables: [],
      config: {
        targetKuikaId: props.sd.importMappedExcelSetupWizardConfig?.targetKuikaId ?? "",
        targetActionId: props.sd.importMappedExcelSetupWizardConfig?.targetActionId ?? "",
        dsId: props.sd.importMappedExcelSetupWizardConfig?.dsId ?? "",
        tableName: props.sd.importMappedExcelSetupWizardConfig?.tableName ?? "",
        primaryKey: props.sd.importMappedExcelSetupWizardConfig?.primaryKey ?? "",
        mappedColumns: props.sd.importMappedExcelSetupWizardConfig?.mappedColumns ?? [],
        parameters
      }
    };
  }

  /** The Kuika primitives a custom parameter can be typed as. */
  private static readonly PARAMETER_TYPES: string[] = [
    "Boolean",
    "DateTimeOffset",
    "Decimal",
    "Double",
    "Guid",
    "Int16",
    "Int32",
    "Int64",
    "String"
  ];

  async componentDidMount() {
    await this.getTables();
    this.migrateMappedColumns();
  }

  migrateMappedColumns = () => {
    const { config } = this.state;

    const selectedTable: IDatabaseTableModel | undefined = this.getSelectedTable();

    if (!selectedTable) {
      return;
    }

    const mappedColumns: IImportMappedExcelColumnMap[] = config.mappedColumns.filter(
      (columnMapping) => !!selectedTable.columns.find((c) => c.name === columnMapping.source)
    );

    mappedColumns.forEach((columnMapping) => {
      if (columnMapping.source === this.state.config.primaryKey) {
        columnMapping.isEnabled = true;
      }
    });

    selectedTable.columns.forEach((column) => {
      if (!mappedColumns.find((c) => c.source === column.name) && !column.isAutoIncrement) {
        mappedColumns.push({
          isEnabled: true,
          source: column.name,
          alias: column.name,
          primaryKey: null,
          isRequired: false
        });
      }
    });

    this.setState({ config: { ...config, mappedColumns } });
  };

  onClose = () => {
    this.props.sd.setImportMappedExcelSetupWizardOpen(false, null);
  };

  private getTables = async () => {
    const managedDbDataSource: IDatasourceDto | undefined = this.props.sd.datasources.find(
      (ds: IDatasourceDto) =>
        ds.datasourceType === EDatasourceType.ManagedDb ||
        ds.datasourceType === EDatasourceType.ManagedDbOracle ||
        ds.datasourceType === EDatasourceType.ManagedDbPostgre
    );

    if (!managedDbDataSource) {
      return;
    }

    try {
      this.setState({ isTablesLoading: true });
      const response = await DatasourceService.GetSchema(managedDbDataSource.id, true);

      if (response.status === 200) {
        this.setState({ tables: response.data.tables ?? [] });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isTablesLoading: false });
    }
  };

  onChangeTable = (option: string | number | null) => {
    const selectedTable = this.state.tables.find((t) => t.name === option);

    if (selectedTable) {
      const config = { ...this.state.config };

      config.tableName = selectedTable.name;
      config.primaryKey = "";
      config.mappedColumns = selectedTable.columns
        .filter((c: IDatabaseColumnModel) => !c.isAutoIncrement)
        .map((c: IDatabaseColumnModel) => ({
          isEnabled: true,
          source: c.name,
          alias: c.name,
          primaryKey: null,
          isRequired: !c.nullable
        }));

      this.setState({ config });
    } else {
      this.setState({
        config: {
          ...this.state.config,
          tableName: "",
          primaryKey: "",
          mappedColumns: []
        }
      });
    }
  };

  onChangePrimaryKey = (option: string | number | null) => {
    const selectedTable: IDatabaseTableModel | undefined = this.getSelectedTable();

    if (!selectedTable) {
      return;
    }

    const column: IDatabaseColumnModel | undefined = selectedTable.columns.find((c) => c.name === option);

    if (!column) {
      return;
    }

    const config = _.cloneDeep(this.state.config);
    config.primaryKey = column.name;

    config.mappedColumns.forEach((columnMapping) => {
      if (columnMapping.source === column.name) {
        columnMapping.isEnabled = true;
        columnMapping.isRequired = true;
      }
    });

    this.setState({ config });
  };

  getSelectedTable = () => {
    const { config } = this.state;
    return this.state.tables.find((t) => t.name === config.tableName);
  };

  onChangeMappedColumnPrimaryKey = (columnMapping: IImportMappedExcelColumnMap, primaryKey: string) => {
    const config = _.cloneDeep(this.state.config);

    config.mappedColumns.forEach((column) => {
      if (column.source === columnMapping.source) {
        column.primaryKey = primaryKey;
      }
    });

    this.setState({ config });
  };

  renderFkPrimaryKeyCell = (columnMapping: IImportMappedExcelColumnMap, column: IDatabaseColumnModel): ReactNode => {
    if (CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(column.kuikaDataType)) {
      return <span className="kuika-import-excel-setup__not-applicable">{"<Not Applicable>"}</span>;
    }

    const schema = column.kuikaDataType.includes(".") ? column.kuikaDataType.split(".")[0] : "dbo";
    const tableName = column.kuikaDataType.includes(".") ? column.kuikaDataType.split(".")[1] : column.kuikaDataType;
    let fkTable: IDatabaseTableModel | undefined;

    if (LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.MSSQL) {
      fkTable = this.state.tables.find((t) => t.schema === schema && t.name === tableName);
    } else {
      fkTable = this.state.tables.find((t) => t.name === tableName);
    }

    const options: KuikaSelectboxOption[] = (fkTable?.columns ?? [])
      .filter(
        (fkColumn: IDatabaseColumnModel) =>
          CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(fkColumn.kuikaDataType) &&
          fkColumn.kuikaDataType.toLowerCase() !== "bytea" &&
          fkColumn.kuikaDataType.toLowerCase() !== "binary"
      )
      .sort((a: IDatabaseColumnModel, b: IDatabaseColumnModel) => {
        if (a.name === "Id") return -1;
        if (b.name === "Id") return 1;
        return a.kuikaDataType.localeCompare(b.name, undefined, { numeric: true });
      })
      .map((fkColumn: IDatabaseColumnModel): KuikaSelectboxOption => ({ label: fkColumn.name, value: fkColumn.name }));

    return (
      <KuikaSelectbox
        size="sm"
        searchable
        dropdownMatchSelectWidth={false}
        placeholder="Select"
        value={columnMapping.primaryKey ?? null}
        options={options}
        onChange={(value: string | number | null): void =>
          this.onChangeMappedColumnPrimaryKey(columnMapping, value === null ? "" : String(value))
        }
      />
    );
  };

  getIsColumnRequired = (columnMapping: IImportMappedExcelColumnMap): boolean => {
    const column = this.getSelectedTable()?.columns.find((c) => c.name === columnMapping.source);

    if (!column) {
      return false;
    }

    if (column.name === "Id") {
      return false;
    }

    return !column.nullable;
  };

  getIsEnabledColumnCheckboxDisabled = (columnMapping: IImportMappedExcelColumnMap): boolean => {
    return this.getIsColumnRequired(columnMapping) || columnMapping.source === this.state.config?.primaryKey;
  };

  getIsRequiredColumnCheckboxDisabled = (columnMapping: IImportMappedExcelColumnMap): boolean => {
    return this.getIsColumnRequired(columnMapping) || columnMapping.source === this.state.config?.primaryKey;
  };

  onChangeIsRequired = (columnMapping: IImportMappedExcelColumnMap, isRequired: boolean) => {
    const config = _.cloneDeep(this.state.config);
    config.mappedColumns.forEach((column) => {
      if (column.source === columnMapping.source) {
        column.isRequired = isRequired;
      }
    });
    this.setState({ config });
  };

  /** The table's own column for a mapping row, or undefined once the table changed under it. */
  private getTableColumnOf = (columnMapping: IImportMappedExcelColumnMap): IDatabaseColumnModel | undefined => {
    return this.getSelectedTable()?.columns.find((c: IDatabaseColumnModel) => c.name === columnMapping.source);
  };

  /** The mapping rows in display order. A copy: sorting state in place would mutate it. */
  private getSortedMappedColumns = (): IImportMappedExcelColumnMap[] => {
    if (!this.getSelectedTable()) {
      return [];
    }

    return [...this.state.config.mappedColumns].sort(
      (a: IImportMappedExcelColumnMap, b: IImportMappedExcelColumnMap) => {
        if (a.source === "Id") return -1; // 'Id' comes first
        if (b.source === "Id") return 1; // 'Id' comes first
        return a.source.localeCompare(b.source, undefined, { numeric: true }); // Alphanumeric sort
      }
    );
  };

  private getColumnMappingColumns = (): KuikaTableColumn<IImportMappedExcelColumnMap>[] => {
    return [
      {
        id: "isEnabled",
        width: 56,
        align: "center",
        header: (): ReactNode => {
          const selectedTable: IDatabaseTableModel | undefined = this.getSelectedTable();

          // Nothing to select all of until a table is picked — and `every` on an empty
          // list answers true, so an unguarded box would sit there ticked over no rows.
          if (!selectedTable || selectedTable.columns.length === 0) {
            return null;
          }

          return (
            <KuikaCheckbox
              checked={this.getIsAllColumnsEnabled()}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => this.setAllColumnsEnabled(e.target.checked)}
              dataTestId="importMappedExcelEnableAllColumns"
            />
          );
        },
        cell: (row: IImportMappedExcelColumnMap): ReactNode => (
          <KuikaCheckbox
            checked={row.isEnabled}
            disabled={this.getIsEnabledColumnCheckboxDisabled(row)}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => this.setColumnEnabled(row, e.target.checked)}
            dataTestId={`importMappedExcelEnableColumn-${row.source}`}
          />
        )
      },
      {
        id: "name",
        width: 180,
        header: (): ReactNode => "Name",
        cell: (row: IImportMappedExcelColumnMap): ReactNode => {
          const column: IDatabaseColumnModel | undefined = this.getTableColumnOf(row);
          const label: string = column ? `${column.name} (${column.kuikaDataType})` : row.source;
          return (
            <span className="kuika-import-excel-setup__column-name" title={label}>
              {label}
            </span>
          );
        }
      },
      {
        id: "alias",
        width: 180,
        header: (): ReactNode => "Alias",
        cell: (row: IImportMappedExcelColumnMap): ReactNode => (
          <KuikaInput
            size="sm"
            value={row.alias}
            onChange={(_e: ChangeEvent<HTMLInputElement>, value: string | number): void =>
              this.onChangeAlias(row, String(value))
            }
            data-testid={`importMappedExcelAlias-${row.source}`}
          />
        )
      },
      {
        id: "defaultValue",
        width: 200,
        header: (): ReactNode => "Default Value",
        cell: (row: IImportMappedExcelColumnMap): ReactNode => this.renderDefaultValueCell(row)
      },
      {
        id: "fkPrimaryKey",
        width: 200,
        header: (): ReactNode => "FK Primary Key",
        cell: (row: IImportMappedExcelColumnMap): ReactNode => {
          const column: IDatabaseColumnModel | undefined = this.getTableColumnOf(row);
          return column ? this.renderFkPrimaryKeyCell(row, column) : null;
        }
      },
      {
        id: "isRequired",
        width: 64,
        align: "center",
        header: (): ReactNode => "Req.",
        cell: (row: IImportMappedExcelColumnMap): ReactNode => (
          <KuikaCheckbox
            checked={row.isRequired}
            disabled={this.getIsRequiredColumnCheckboxDisabled(row)}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => this.onChangeIsRequired(row, e.target.checked)}
            dataTestId={`importMappedExcelRequiredColumn-${row.source}`}
          />
        )
      }
    ];
  };

  onChangeAlias = (columnMapping: IImportMappedExcelColumnMap, alias: string) => {
    const config = _.cloneDeep(this.state.config);
    config.mappedColumns.forEach((column) => {
      if (column.source === columnMapping.source) {
        column.alias = alias;
      }
    });
    this.setState({ config });
  };

  onChangeDefaultValue = (columnMapping: IImportMappedExcelColumnMap, value: string) => {
    const config = _.cloneDeep(this.state.config);
    config.mappedColumns.forEach((column) => {
      if (column.source === columnMapping.source) {
        column.defaultValue = value || null;
      }
    });
    this.setState({ config });
  };

  getDefaultValueOptions = (): IDefaultValueOption[] => {
    const systemOptions: IDefaultValueOption[] = [
      { value: "@kuikausername", label: "Current Username" },
      { value: "@kuikauserid", label: "Current User Id" }
    ];

    // Use filtered parameters to exclude reserved names
    const parameterOptions: IDefaultValueOption[] = this.getFilteredParameters().map(
      (param: IImportMappedExcelParameter): IDefaultValueOption => ({
        value: `@${param.name}`,
        label: `Parameter: ${param.name} (${param.type})`
      })
    );

    return [...systemOptions, ...parameterOptions];
  };

  /**
   * A default value is typed OR picked. The field stays the text field it always was — the
   * value it holds is the literal `@kuikausername` / `@parameter` the action reads — and the
   * menu beside it writes one of those tokens in, so neither way of answering is hidden.
   */
  renderDefaultValueCell = (columnMapping: IImportMappedExcelColumnMap): ReactNode => {
    const isPrimaryKey: boolean = columnMapping.source === this.state.config?.primaryKey;
    const isDisabled: boolean = !columnMapping.isEnabled || isPrimaryKey;
    const options: KuikaDropdownMenuItemType[] = this.getDefaultValueOptions().map(
      (option: IDefaultValueOption): KuikaDropdownMenuItemType => ({
        key: option.value,
        label: option.label,
        onClick: (): void => this.onChangeDefaultValue(columnMapping, option.value)
      })
    );

    return (
      <KuikaInput
        size="sm"
        placeholder="Select or type a value"
        disabled={isDisabled}
        value={columnMapping.defaultValue ?? ""}
        onChange={(_e: ChangeEvent<HTMLInputElement>, value: string | number): void =>
          this.onChangeDefaultValue(columnMapping, String(value))
        }
        data-testid={`importMappedExcelDefaultValue-${columnMapping.source}`}
        rightUnit={
          <KuikaDropdownMenu options={options} placement="bottomRight">
            <KuikaButton
              type="tertiary"
              color="neutral"
              size="sm"
              isIcon
              disabled={isDisabled}
              aria-label="Pick a default value"
              data-testid={`importMappedExcelDefaultValueMenu-${columnMapping.source}`}
            >
              {/* The selectbox's own arrow, so a field you can also pick from reads like one. */}
              <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} color="var(--dark-7)" />
            </KuikaButton>
          </KuikaDropdownMenu>
        }
      />
    );
  };

  getIsAllColumnsEnabled = (): boolean => {
    const { config } = this.state;
    const selectedTable = this.getSelectedTable();
    return config.mappedColumns
      .filter((c) => {
        const tableColumn = selectedTable?.columns.find((column) => c.source === column.name);
        return tableColumn?.nullable || tableColumn?.name === "Id";
      })
      .every((column) => column.isEnabled);
  };

  getIsAllColumnsDisabled = (): boolean => {
    const { config } = this.state;
    const selectedTable = this.getSelectedTable();
    return config.mappedColumns
      .filter((c) => {
        const tableColumn = selectedTable?.columns.find((column) => c.source === column.name);
        return tableColumn?.nullable || tableColumn?.name === "Id";
      })
      .every((column) => !column.isEnabled);
  };

  setAllColumnsEnabled = (isEnabled: boolean) => {
    const config = _.cloneDeep(this.state.config);
    const selectedTable = this.getSelectedTable();
    config.mappedColumns
      .filter((c) => {
        const tableColumn = selectedTable?.columns.find((column) => c.source === column.name);
        return !tableColumn?.nullable || tableColumn?.name === "Id";
      })
      .forEach((column) => (column.isEnabled = isEnabled));
    this.setState({ config });
  };

  setColumnEnabled = (columnMapping: IImportMappedExcelColumnMap, isEnabled: boolean) => {
    const config = _.cloneDeep(this.state.config);
    config.mappedColumns.forEach((column) => {
      if (column.source === columnMapping.source) {
        column.isEnabled = isEnabled;
      }
    });
    this.setState({ config });
  };

  getIsColumnUnique = (column: IDatabaseColumnModel) => {
    if (column.name === "Id") {
      return true;
    }

    return column.isUnique;
  };

  getPrimaryKeyOptions = () => {
    const selectedTable = this.getSelectedTable();
    return (
      selectedTable?.columns.filter((column) => {
        const dataType = column.kuikaDataType.toLowerCase();
        return (
          dataType === "guid" ||
          dataType === "int16" ||
          dataType === "int32" ||
          dataType === "int64" ||
          dataType === "string"
        );
      }) ?? []
    );
  };

  getIsAnyColumnsAliasIsEmpty = (): boolean => {
    return this.state.config.mappedColumns.filter((c) => c.isEnabled).some((c) => _.isEmpty(c.alias));
  };

  getIsAnyFkColumnsPrimaryKeyIsEmpty = (): boolean => {
    return this.state.config.mappedColumns
      .filter((mc) => {
        const column = this.getSelectedTable()?.columns.find((c) => c.name === mc.source);
        return column && mc.isEnabled && !CommonHelperFunctions.getIsTypeSimpleKuikaPrimitive(column.kuikaDataType);
      })
      .some((c) => _.isEmpty(c.primaryKey));
  };

  getIsNextButtonDisabled = (): boolean => {
    const { config } = this.state;
    return (
      _.isEmpty(config.tableName) ||
      _.isEmpty(config.primaryKey) ||
      this.getIsAllColumnsDisabled() ||
      this.getIsAnyColumnsAliasIsEmpty() ||
      this.getIsAnyFkColumnsPrimaryKeyIsEmpty()
    );
  };

  onClickNext = () => {
    const dict = this.props.sd.screenDesignDictionary;
    const di = dict[parseInt(this.state.config.targetKuikaId, 10)];

    if (!di) {
      throw new Error("Display Item not found");
    }

    const action: IAction | undefined = ScreenDesignHelper.findActionById(this.state.config.targetActionId, di);

    if (!action) {
      throw new Error("Action not found");
    }

    const managedDbDataSource: IDatasourceDto | undefined = this.props.sd.datasources.find((ds: IDatasourceDto) => {
      return (
        ds.datasourceType === EDatasourceType.ManagedDb ||
        ds.datasourceType === EDatasourceType.ManagedDbOracle ||
        ds.datasourceType === EDatasourceType.ManagedDbPostgre
      );
    });

    if (!managedDbDataSource) {
      throw new Error("ManagedDb DataSource not found");
    }

    const dsIdParam = action.parameters.find((p) => p.name === "dsId");

    if (!dsIdParam) {
      throw new Error("dsId parameter not found");
    }

    dsIdParam.symbol.fixedValue = managedDbDataSource.id;

    const tableNameParam = action.parameters.find((p) => p.name === "tableName");

    if (!tableNameParam) {
      throw new Error("tableName parameter not found");
    }

    tableNameParam.symbol.fixedValue = this.state.config.tableName;

    const primaryKeyParam = action.parameters.find((p) => p.name === "primaryKey");

    if (!primaryKeyParam) {
      throw new Error("primaryKey parameter not found");
    }

    primaryKeyParam.symbol.fixedValue = this.state.config.primaryKey;

    const mappedColumnsParam = action.parameters.find((p) => p.name === "mappedColumns");

    if (!mappedColumnsParam) {
      throw new Error("mappedColumns parameter not found");
    }

    mappedColumnsParam.symbol.fixedValue = JSON.stringify(this.state.config.mappedColumns);

    const parametersParam = action.parameters.find((p) => p.name === "parameters");

    if (!parametersParam) {
      throw new Error("parameters parameter not found");
    }

    // Get filtered parameters (excluding reserved names)
    const reservedNames = ["dsId", "tableName", "primaryKey", "mappedColumns", "parameters"];
    const filteredParameters = (this.state.config.parameters ?? []).filter((p) => !reservedNames.includes(p.name));

    // Set parameters metadata JSON
    parametersParam.symbol.fixedValue = JSON.stringify(filteredParameters);

    // Add custom parameters to the action
    if (filteredParameters.length > 0) {
      filteredParameters.forEach((param) => {
        let existingParam = action.parameters.find((p) => p.name === param.name);

        if (!existingParam) {
          // Create new parameter
          action.parameters.push({
            name: param.name,
            Typename: param.type as BackEndValueType,
            cardinality: 1,
            symbol: {
              category: 0, // ESymbolCategory.fixed
              fixedValue: ""
            }
          } as IActionParameter);
        } else {
          // Update existing parameter type if changed
          existingParam.Typename = param.type as BackEndValueType;
        }
      });
    }

    // Remove custom parameters that are no longer in the config
    const parameterNames = filteredParameters.map((p) => p.name);
    action.parameters = action.parameters.filter((p) => {
      // Keep standard parameters
      if (["dsId", "tableName", "primaryKey", "mappedColumns", "parameters"].includes(p.name)) {
        return true;
      }
      // Keep custom parameters that are still in the config
      return parameterNames.includes(p.name);
    });

    this.props.sd.setScreenDesign(this.props.sd.getScreenDesign(), ESdChangeReason.DiActionUpdated);
    KMainFunctions.displaySuccessNotification("Import Mapped Excel saved successfully");
    this.onClose();
  };

  getIsLoading = (): boolean => {
    return this.state.isTablesLoading;
  };

  // Get filtered parameters excluding reserved names
  getFilteredParameters = (): IImportMappedExcelParameter[] => {
    const reservedNames = ["dsId", "tableName", "primaryKey", "mappedColumns", "parameters"];
    return (this.state.config.parameters ?? []).filter((p) => !reservedNames.includes(p.name));
  };

  // Parameter management methods
  addParameter = () => {
    const config = _.cloneDeep(this.state.config);

    // Limit to 15 custom parameters (matching available placeholders a5-a19)
    if (this.getFilteredParameters().length >= MAX_PARAMETER_COUNT) {
      KMainFunctions.displayErrorNotification(`Maximum of ${MAX_PARAMETER_COUNT} parameters allowed`);
      return;
    }

    let counter = 1;
    let newParameterName = `Parameter${counter}`;

    // Find a unique name
    while (config.parameters?.some((p) => p.name === newParameterName)) {
      counter++;
      newParameterName = `Parameter${counter}`;
    }

    if (!config.parameters) {
      config.parameters = [];
    }

    config.parameters.push({
      id: _.uniqueId("param_"),
      name: newParameterName,
      type: "String" // Default type
    });

    this.setState({ config });
  };

  deleteParameter = (parameter: IImportMappedExcelParameter) => {
    const config = _.cloneDeep(this.state.config);
    config.parameters = config.parameters?.filter((p) => p.id !== parameter.id) ?? [];
    this.setState({ config });
  };

  onChangeParameterName = (parameter: IImportMappedExcelParameter, newName: string) => {
    // Reserved parameter names that cannot be used
    const reservedNames = ["dsId", "tableName", "primaryKey", "mappedColumns", "parameters"];

    if (reservedNames.includes(newName)) {
      KMainFunctions.displayErrorNotification(`"${newName}" is a reserved parameter name and cannot be used`);
      return;
    }

    const config = _.cloneDeep(this.state.config);
    const param = config.parameters?.find((p) => p.id === parameter.id);
    if (param) {
      param.name = newName;
    }
    this.setState({ config });
  };

  onChangeParameterType = (parameter: IImportMappedExcelParameter, newType: string) => {
    const config = _.cloneDeep(this.state.config);
    const param = config.parameters?.find((p) => p.id === parameter.id);
    if (param) {
      param.type = newType;
    }
    this.setState({ config });
  };

  private getParameterColumns = (): KuikaTableColumn<IImportMappedExcelParameter>[] => {
    const typeOptions: KuikaSelectboxOption[] = ImportMappedExcelSetupWizard.PARAMETER_TYPES.map(
      (type: string): KuikaSelectboxOption => ({ label: type, value: type })
    );

    return [
      {
        id: "name",
        width: 380,
        header: (): ReactNode => "Name",
        cell: (row: IImportMappedExcelParameter): ReactNode => (
          <KuikaInput
            size="sm"
            placeholder="Parameter Name"
            value={row.name}
            onChange={(_e: ChangeEvent<HTMLInputElement>, value: string | number): void =>
              this.onChangeParameterName(row, String(value))
            }
            data-testid={`importMappedExcelParameterName-${row.id}`}
          />
        )
      },
      {
        id: "type",
        width: 380,
        header: (): ReactNode => "Type",
        cell: (row: IImportMappedExcelParameter): ReactNode => (
          <KuikaSelectbox
            size="sm"
            dropdownMatchSelectWidth={false}
            value={row.type}
            options={typeOptions}
            onChange={(value: string | number | null): void =>
              this.onChangeParameterType(row, value === null ? "" : String(value))
            }
          />
        )
      },
      {
        id: "delete",
        width: 64,
        align: "center",
        header: (): ReactNode => null,
        cell: (row: IImportMappedExcelParameter): ReactNode => (
          <KuikaTooltip content="Delete parameter">
            <KuikaButton
              type="tertiary"
              color="neutral"
              size="sm"
              isIcon
              aria-label="Delete parameter"
              onClick={(): void => this.deleteParameter(row)}
              data-testid={`importMappedExcelDeleteParameter-${row.id}`}
            >
              <KuikaSvg name="trash" width={16} height={16} />
            </KuikaButton>
          </KuikaTooltip>
        )
      }
    ];
  };

  private renderAddParameterButton = (): ReactNode => {
    const isDisabled: boolean = this.getFilteredParameters().length >= MAX_PARAMETER_COUNT;
    const button: ReactNode = (
      <KuikaButton
        type="tertiary"
        size="sm"
        disabled={isDisabled}
        onClick={this.addParameter}
        data-testid="importMappedExcelAddParameter"
      >
        <span className="kuika-flex-center--gap-xs">
          <KuikaSvg name="plus" width={14} height={14} />
          Add Parameter
        </span>
      </KuikaButton>
    );

    if (!isDisabled) {
      return button;
    }

    // A disabled button fires no pointer events, so the reason has to hang off a wrapper.
    return (
      <KuikaTooltip content={`Maximum ${MAX_PARAMETER_COUNT} parameters allowed`}>
        <span>{button}</span>
      </KuikaTooltip>
    );
  };

  private getTableOptions = (): KuikaSelectboxOption[] => {
    return this.state.tables
      .filter((table: IDatabaseTableModel) => !KMainFunctions.getDefaultKuikaTableNames().includes(table.name))
      .map((table: IDatabaseTableModel): KuikaSelectboxOption => ({ label: table.name, value: table.name }));
  };

  private getPrimaryKeySelectboxOptions = (): KuikaSelectboxOption[] => {
    return this.getPrimaryKeyOptions().map((column: IDatabaseColumnModel): KuikaSelectboxOption => {
      const name: string = `${column.name} (${column.kuikaDataType})`;

      return {
        value: column.name,
        // `searchLabel` keeps the option findable by typing: the label is a node.
        searchLabel: name,
        label: (
          <span className="kuika-import-excel-setup__key-option">
            {name}
            {this.getIsColumnUnique(column) && <KuikaBadge size="small" color="grey" content="Unique" />}
          </span>
        )
      };
    });
  };

  render(): ReactNode {
    return (
      <KModal
        showModal
        header="Import Mapped Excel Setup"
        closeModal={this.onClose}
        width={900}
        className="kuika-import-excel-setup"
        isLoading={this.getIsLoading()}
        footer={
          <KModalFooter
            submitLabel="Next"
            submitTestId="importMappedExcelNextButton"
            onSubmit={this.onClickNext}
            disabled={this.getIsNextButtonDisabled()}
            onCancel={this.onClose}
          />
        }
      >
        <div className="kuika-import-excel-setup__body">
          <KuikaH8 className="kuika-import-excel-setup__section-title">Select ManagedDB Table</KuikaH8>
          <KuikaSmallMediumText className="kuika-import-excel-setup__section-description">
            Map your Excel columns to the target table for a smooth and accurate data import. Customize mappings and
            define the primary key as needed.
          </KuikaSmallMediumText>
          <div className="kuika-import-excel-setup__table-picker">
            <KuikaSelectbox
              placeholder="Select Table"
              searchable
              options={this.getTableOptions()}
              disabled={this.state.isTablesLoading}
              value={_.isEmpty(this.state.config.tableName) ? null : this.state.config.tableName}
              onChange={this.onChangeTable}
              data-testid="importMappedExcelTable"
            />
            <KuikaSelectbox
              placeholder="Select Primary Key"
              searchable
              options={this.getPrimaryKeySelectboxOptions()}
              disabled={this.state.isTablesLoading || _.isEmpty(this.state.config.tableName)}
              value={_.isEmpty(this.state.config.primaryKey) ? null : this.state.config.primaryKey}
              onChange={this.onChangePrimaryKey}
              data-testid="importMappedExcelPrimaryKey"
            />
          </div>

          <KuikaH8 className="kuika-import-excel-setup__section-title">Column Mapping</KuikaH8>
          <KuikaTable<IImportMappedExcelColumnMap>
            data={this.getSortedMappedColumns()}
            columns={this.getColumnMappingColumns()}
            rowKey={(row: IImportMappedExcelColumnMap): string => row.source}
            fitContentHeight
            enablePagination={false}
            emptyTitle="No table is selected."
            data-testid="importMappedExcelColumnMapping"
          />

          <div className="kuika-import-excel-setup__section-row">
            <KuikaH8 className="kuika-import-excel-setup__section-title">Parameters</KuikaH8>
            {this.renderAddParameterButton()}
          </div>
          <KuikaTable<IImportMappedExcelParameter>
            data={this.getFilteredParameters()}
            columns={this.getParameterColumns()}
            rowKey={(row: IImportMappedExcelParameter): string => row.id ?? row.name}
            fitContentHeight
            enablePagination={false}
            emptyTitle="No parameters defined."
            data-testid="importMappedExcelParameters"
          />
        </div>
      </KModal>
    );
  }
}

const importMappedExcelSetupWizard = withSD<IImportMappedExcelSetupWizardProps>(ImportMappedExcelSetupWizard);
export { importMappedExcelSetupWizard as ImportMappedExcelSetupWizard };
